import React, { useContext, useEffect, useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useTheme,
} from '@material-ui/core';

import { MAIN_CONTAINER_ID } from '../../../../constants';
import { DeviceContext } from '../../../../contexts';
import { AgreementVersion } from '../../../../types';
import { AppTextField } from '../../../../components/inputs';
import { ButtonTitleWithLoading } from '../../../../components/ButtonTitleWithLoading';

export type VersionActionMode = 'edit-note' | 'make-current' | 'hide';

interface Props {
  open: boolean;
  mode: VersionActionMode;
  version: AgreementVersion | null;
  loading: boolean;
  handleClose: () => void;
  // Receives the note (edit-note/make-current) or the reason (hide).
  handleConfirm: (text: string) => void;
}

const MODE_COPY: Record<
  VersionActionMode,
  { title: string; confirmText: string; fieldLabel: string; requiredMessage?: string }
> = {
  'edit-note': {
    title: 'Edit note',
    confirmText: 'Save note',
    fieldLabel: 'Note',
  },
  'make-current': {
    title: 'Make version current',
    confirmText: 'Make current',
    fieldLabel: 'Note (optional)',
  },
  hide: {
    title: 'Hide version',
    confirmText: 'Hide version',
    fieldLabel: 'Reason',
    requiredMessage: 'Reason is required',
  },
};

const getSubtitle = (mode: VersionActionMode, version: AgreementVersion | null): string => {
  if (!version) {
    return '';
  }
  if (mode === 'make-current') {
    return `This will make version ${version.version} the current agreement${
      version.hidden_at ? ' and unhide it' : ''
    }. The worker's agreement link will point at this document.`;
  }
  if (mode === 'hide') {
    return `Hidden versions stay in the history but are greyed out. Nothing is deleted — version ${version.version} can be made current again later.`;
  }
  return 'Only the note changes. The document and its dates stay exactly as they are.';
};

export const VersionActionDialog: React.FC<Props> = ({
  open,
  mode,
  version,
  loading,
  handleClose,
  handleConfirm,
}) => {
  const { isMobile } = useContext(DeviceContext);
  const theme = useTheme();
  const [text, setText] = useState<string>('');
  const [error, setError] = useState<string>('');

  const copy = MODE_COPY[mode];
  const destructiveStyle =
    mode === 'hide' && !loading
      ? { backgroundColor: theme.palette.error.main, color: theme.palette.error.contrastText }
      : undefined;

  useEffect(() => {
    if (open) {
      setText(mode === 'edit-note' ? version?.note || '' : '');
      setError('');
    }
  }, [open, mode, version]);

  const onConfirm = () => {
    if (copy.requiredMessage && !text.trim()) {
      setError(copy.requiredMessage);
      return;
    }
    handleConfirm(text.trim());
  };

  return (
    <Dialog
      fullScreen={isMobile}
      open={open}
      maxWidth="sm"
      fullWidth
      container={document.getElementById(MAIN_CONTAINER_ID)}
      onClose={handleClose}
    >
      <DialogTitle>{copy.title}</DialogTitle>

      <DialogContent>
        <DialogContentText>{getSubtitle(mode, version)}</DialogContentText>

        <AppTextField
          multiline
          fullWidth
          label={copy.fieldLabel}
          value={text}
          error={!!error}
          helperText={error}
          onChange={e => {
            setText(e.target.value);
            setError('');
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button size="large" style={{ minWidth: 115 }} variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          size="large"
          style={{ minWidth: 115, ...destructiveStyle }}
          color="primary"
          variant="contained"
          disabled={loading}
          onClick={onConfirm}
        >
          <ButtonTitleWithLoading title={copy.confirmText} loaderVariant="paper" loading={loading} />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
